import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import useInfiniteBooks, { flattenBooks } from "api/BookResource/useInfiniteBooks";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import { prefetchTags } from "api/TagResource/useTags";
import useAuthStore from "store/authStore";
import useTokenStore from "store/tokenStore";
import useInfiniteScrollSentinel from "hooks/useInfiniteScrollSentinel";
import useBooksFilters from "./useBooksFilters";
import { countActiveFilters, filtersPagePath } from "./booksQueryParams";

const QUERY_FIELDS = {
	category_is: "categoryIs", genre_is: "genreIs", tag_is: "tagIs", publisher_is: "publisherIs",
	pricing_model_is: "pricingModelIs", country_is: "countryIs", publication_date_from: "publicationDateFrom",
	publication_date_to: "publicationDateTo", price_min: "priceMin", price_max: "priceMax", format: "format",
	availability: "availability", page_count_min: "pageCountMin", page_count_max: "pageCountMax", language_is: "language",
};

export default function useCatalogueBooks() {
	const filters = useBooksFilters();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();
	const loggedIn = useAuthStore((state) => state.loggedIn);
	const { data: categories } = useFetchCategories();
	const { data: publishers } = useFetchPublishers();
	const params = useMemo(() => ({
		...Object.fromEntries(Object.entries(QUERY_FIELDS).map(([key, field]) => [`filter[${key}]`, filters[field]])),
		searchTerm: filters.searchTerm,
	}), [filters]);
	const query = useInfiniteBooks(params);
	const books = useMemo(() => flattenBooks(query.data), [query.data]);
	const total = query.data?.pages?.[0]?.totalRecords ?? 0;
	const { hasNextPage, isFetching, isError, fetchNextPage } = query;
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetching && !isError) fetchNextPage();
	}, [hasNextPage, isFetching, isError, fetchNextPage]);
	const sentinelRef = useInfiniteScrollSentinel({
		onLoadMore: loadMore,
		enabled: Boolean(query.hasNextPage) && !query.isFetching && !query.isLoading && !query.isError,
	});
	const selectedTags = useMemo(() => filters.tagIs.split("*").filter(Boolean), [filters.tagIs]);
	const toggleTag = (name) => filters.changeTagIs((selectedTags.includes(name)
		? selectedTags.filter((tag) => tag !== name) : [...selectedTags, name]).join("*"));
	const prefetch = useCallback(() => {
		prefetchTags(queryClient, accessToken, "no", { page: 1, rowsPerPage: 40, sort: "usage" });
	}, [queryClient, accessToken]);
	return {
		filters, query, books, total, loggedIn, sentinelRef, loadMore, selectedTags, toggleTag, prefetch,
		categories: categories?.data, publishers: publishers?.data,
		filtersPath: filtersPagePath(location.search), filterCount: countActiveFilters(filters),
		clearAll: () => filters.changeAllFiltersAtOnce({}),
	};
}
